import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BuildingOfficeIcon, MapPinIcon } from '@heroicons/react/20/solid'
import PlacowkaService from '@/services/PlacowkaService'
import MiastoFilterModal from '@/filter-modals/components/MiastoFilterModal'
import GminaFilterModal from '@/filter-modals/components/GminaFilterModal'
import PowiatFilterModal from '@/filter-modals/components/PowiatFilterModal'
import WojewodztwoFilterModal from '@/filter-modals/components/WojewodztwoFilterModal'
import TypFilterModal from '@/filter-modals/components/TypFilterModal'
import { getFilterIndicator, getSortIndicator } from '@/lib/tableHelpers'

const truncate = value => {
	const text = value || '—'
	return text.length > 50 ? `${text.slice(0, 47)}...` : text
}

export default function PlacowkaPicker({ onPlacowkaSelect, onChoose }) {
	const defaultLimit = 5
	const { t } = useTranslation()
	const [searchTerm, setSearchTerm] = useState('')
	const [sortColumn, setSortColumn] = useState('_id')
	const [sortDirection, setSortDirection] = useState('desc')
	const [isMiastoFilterOpen, setIsMiastoFilterOpen] = useState(false)
	const [selectedMiastoId, setSelectedMiastoId] = useState(null)
	const [isGminaFilterOpen, setIsGminaFilterOpen] = useState(false)
	const [selectedGminaId, setSelectedGminaId] = useState(null)
	const [isPowiatFilterOpen, setIsPowiatFilterOpen] = useState(false)
	const [selectedPowiatId, setSelectedPowiatId] = useState(null)
	const [isWojewodztwoFilterOpen, setIsWojewodztwoFilterOpen] = useState(false)
	const [selectedWojewodztwoId, setSelectedWojewodztwoId] = useState(null)
	const [isTypFilterOpen, setIsTypFilterOpen] = useState(false)
	const [selectedTypId, setSelectedTypId] = useState(null)
	const [placowkiData, setPlacowkiData] = useState([])
	const [loadingPlacowki, setLoadingPlacowki] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)
	const [hasMore, setHasMore] = useState(false)
	const [limit, setLimit] = useState(defaultLimit)

	const loadPlacowkiData = useCallback(async () => {
		if (!loadingMore) setLoadingPlacowki(true)
		setError(null)

		const trimmed = searchTerm.trim()
		const requestOptions = {
			limit,
			sortColumn,
			sortDirection,
			filters: {
				id_miasto: selectedMiastoId || null,
				id_gmina: selectedGminaId || null,
				id_powiat: selectedPowiatId || null,
				id_wojewodztwo: selectedWojewodztwoId || null,
				id_typ: selectedTypId || null,
			},
		}

		const result = trimmed
			? await PlacowkaService.search(trimmed, requestOptions)
			: await PlacowkaService.getAll(requestOptions)

		if (result.error) {
			setError(result.error)
			setPlacowkiData([])
			setHasMore(false)
		} else {
			setPlacowkiData(Array.isArray(result.data) ? result.data : [])
			setHasMore(!!result.hasMore)
		}

		setLoadingPlacowki(false)
		setLoadingMore(false)
	}, [
		limit,
		loadingMore,
		searchTerm,
		selectedMiastoId,
		selectedGminaId,
		selectedPowiatId,
		selectedWojewodztwoId,
		selectedTypId,
		sortColumn,
		sortDirection,
	])

	useEffect(() => {
		loadPlacowkiData()
	}, [loadPlacowkiData])

	const handleSearchChange = useCallback(event => {
		setSearchTerm(event.target.value)
		setLimit(defaultLimit)
	}, [defaultLimit])

	const handleSelect = useCallback(placowka => {
		const cb = onPlacowkaSelect || onChoose
		if (cb) cb(placowka)
	}, [onChoose, onPlacowkaSelect])

	const handleKeyDown = event => {
		if (event.key === 'Enter') {
			event.preventDefault()
			if (placowkiData.length === 1) {
				handleSelect(placowkiData[0])
			}
		}
	}

	const handleLoadMore = () => {
		if (!hasMore || loadingMore) return
		setLoadingMore(true)
		setLimit(prev => prev + defaultLimit)
	}

	const handleSort = useCallback(column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSortColumn(column)
		setSortDirection(newDirection)
	}, [sortColumn, sortDirection])

	const handleApplyMiastoFilter = useCallback(miastoId => {
		setSelectedMiastoId(miastoId || null)
		setLimit(defaultLimit)
		setIsMiastoFilterOpen(false)
	}, [defaultLimit])

	const handleApplyGminaFilter = useCallback(
		gminaId => {
			const nextGminaId = gminaId || null
			setSelectedGminaId(nextGminaId)
			if (nextGminaId !== selectedGminaId) setSelectedMiastoId(null)
			setLimit(defaultLimit)
			setIsGminaFilterOpen(false)
		},
		[defaultLimit, selectedGminaId],
	)

	const handleApplyPowiatFilter = useCallback(
		powiatId => {
			const nextPowiatId = powiatId || null
			setSelectedPowiatId(nextPowiatId)
			if (nextPowiatId !== selectedPowiatId) {
				setSelectedGminaId(null)
				setSelectedMiastoId(null)
			}
			setLimit(defaultLimit)
			setIsPowiatFilterOpen(false)
		},
		[defaultLimit, selectedPowiatId],
	)

	const handleApplyWojewodztwoFilter = useCallback(
		wojewodztwoId => {
			const nextWojewodztwoId = wojewodztwoId || null
			setSelectedWojewodztwoId(nextWojewodztwoId)
			if (nextWojewodztwoId !== selectedWojewodztwoId) {
				setSelectedPowiatId(null)
				setSelectedGminaId(null)
				setSelectedMiastoId(null)
			}
			setLimit(defaultLimit)
			setIsWojewodztwoFilterOpen(false)
		},
		[defaultLimit, selectedWojewodztwoId],
	)

	const handleApplyTypFilter = useCallback(typId => {
		setSelectedTypId(typId || null)
		setLimit(defaultLimit)
		setIsTypFilterOpen(false)
	}, [defaultLimit])

	return (
		<div className="space-y-4">
			<div className="mb-4">
				<input
					type="text"
					value={searchTerm}
					onChange={handleSearchChange}
					onKeyDown={handleKeyDown}
					placeholder={t('placowkaPicker.searchPlaceholder')}
					className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
				/>
			</div>

			{error && (
				<div className="bg-red-50 border border-red-200 rounded-md p-3">
					<div className="text-sm text-red-600">{t('placowkaPicker.errorTitle')}: {String(error)}</div>
				</div>
			)}

			{loadingPlacowki ? (
				<div className="text-center py-4 text-gray-500">{t('placowkaPicker.loading')}</div>
			) : placowkiData.length === 0 ? (
				<div className="text-center py-4 text-gray-500">{t('placowkaPicker.empty')}</div>
			) : (
				<div className="overflow-x-auto">
					<table className="min-w-full bg-white border border-gray-300 rounded-lg shadow">
						<thead>
							<tr className="bg-gray-50">
							<th className="py-3 px-4 border-b border-gray-300 text-left w-24 font-semibold text-gray-700 whitespace-nowrap">{t('placowkaPicker.table.action')}</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left whitespace-nowrap">
									<button
										type="button"
										className="flex items-center gap-1 font-semibold text-gray-700 hover:text-blue-600 transition-colors duration-200 focus:outline-none focus:text-blue-600"
										onClick={() => handleSort('nazwa')}
									>
										{t('placowkaPicker.table.name')}
										{getSortIndicator('nazwa', sortColumn, sortDirection)}
									</button>
								</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left whitespace-nowrap">
									<div className="inline-flex items-center gap-1">
										<span className="font-semibold text-gray-700">{t('placowkaPicker.table.city')}</span>
										<button
											type="button"
											className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
											onClick={() => setIsMiastoFilterOpen(true)}
											title={t('placowkaPicker.filters.buttonTitle')}
										>
											{getFilterIndicator(!!selectedMiastoId)}
										</button>
									</div>
								</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left whitespace-nowrap">
									<div className="inline-flex items-center gap-1">
										<span className="font-semibold text-gray-700">{t('placowkaPicker.table.gmina')}</span>
										<button
											type="button"
											className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
											onClick={() => setIsGminaFilterOpen(true)}
											title={t('placowkaPicker.filters.buttonTitle')}
										>
											{getFilterIndicator(!!selectedGminaId)}
										</button>
									</div>
								</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left whitespace-nowrap">
									<div className="inline-flex items-center gap-1">
										<span className="font-semibold text-gray-700">{t('placowkaPicker.table.powiat')}</span>
										<button
											type="button"
											className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
											onClick={() => setIsPowiatFilterOpen(true)}
											title={t('placowkaPicker.filters.buttonTitle')}
										>
											{getFilterIndicator(!!selectedPowiatId)}
										</button>
									</div>
								</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left whitespace-nowrap">
									<div className="inline-flex items-center gap-1">
										<span className="font-semibold text-gray-700">{t('placowkaPicker.table.voivodeship')}</span>
										<button
											type="button"
											className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
											onClick={() => setIsWojewodztwoFilterOpen(true)}
											title={t('placowkaPicker.filters.buttonTitle')}
										>
											{getFilterIndicator(!!selectedWojewodztwoId)}
										</button>
									</div>
								</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left whitespace-nowrap">
									<div className="inline-flex items-center gap-1">
										<span className="font-semibold text-gray-700">{t('placowkaPicker.table.type')}</span>
										<button
											type="button"
											className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
											onClick={() => setIsTypFilterOpen(true)}
											title={t('placowkaPicker.filters.buttonTitle')}
										>
											{getFilterIndicator(!!selectedTypId)}
										</button>
									</div>
								</th>
							</tr>
						</thead>
						<tbody>
							{placowkiData.map(placowka => (
								<tr key={placowka._id || placowka.id} className="hover:bg-gray-50">
									<td className="py-3 px-4 border-b border-gray-300 whitespace-nowrap">
										<button
											type="button"
											onClick={() => handleSelect(placowka)}
											className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
										>
											{t('placowkaPicker.select')}
										</button>
									</td>
									<td className="py-3 px-4 border-b border-gray-300">
										<div className="flex flex-col gap-1">
											<div
												className="flex items-center gap-2 whitespace-nowrap font-medium text-gray-900"
												title={placowka.nazwa || undefined}>
												<BuildingOfficeIcon className="h-4 w-4 shrink-0 text-gray-400" />
												<span>{truncate(placowka.nazwa)}</span>
											</div>
											<div
												className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-600"
												title={placowka.adres || undefined}>
												<MapPinIcon className="h-4 w-4 shrink-0 text-gray-400" />
												<span>{truncate(placowka.adres)}</span>
											</div>
										</div>
									</td>
									<td className="py-3 px-4 border-b border-gray-300 whitespace-nowrap">
										<span className="text-gray-900">{placowka.id_miasto?.nazwa || '—'}</span>
									</td>
									<td className="py-3 px-4 border-b border-gray-300 whitespace-nowrap">
										<span className="text-gray-900">{placowka.id_gmina?.nazwa || '—'}</span>
									</td>
									<td className="py-3 px-4 border-b border-gray-300 whitespace-nowrap">
										<span className="text-gray-900">{placowka.id_powiat?.nazwa || '—'}</span>
									</td>
									<td className="py-3 px-4 border-b border-gray-300 whitespace-nowrap">
										<span className="text-gray-900">{placowka.id_wojewodztwo?.nazwa || '—'}</span>
									</td>
									<td className="py-3 px-4 border-b border-gray-300 whitespace-nowrap">
										<span className="text-gray-900">{placowka.id_typ?.typ || '—'}</span>
									</td>

								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{hasMore && (
				<div className="text-center">
					<button
						type="button"
						onClick={handleLoadMore}
						className="px-4 py-2 text-sm font-medium text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50 disabled:opacity-60"
						disabled={loadingMore}
					>
						{loadingMore ? t('placowkaPicker.loadingMore') : t('placowkaPicker.loadMore')}
					</button>
				</div>
			)}

			<MiastoFilterModal
				isOpen={isMiastoFilterOpen}
				onClose={() => setIsMiastoFilterOpen(false)}
				selectedMiastoId={selectedMiastoId}
				idGmina={selectedGminaId}
				idPowiat={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyMiastoFilter}
				title={t('placowkaPicker.filters.cityTitle')}
				description={t('placowkaPicker.filters.cityDescription')}
				applyLabel={t('placowkaPicker.filters.select')}
				clearLabel={t('placowkaPicker.filters.clear')}
			/>
			<GminaFilterModal
				isOpen={isGminaFilterOpen}
				onClose={() => setIsGminaFilterOpen(false)}
				selectedGminaId={selectedGminaId}
				idPowiat={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyGminaFilter}
				title={t('placowkaPicker.filters.gminaTitle')}
				description={t('placowkaPicker.filters.gminaDescription')}
				applyLabel={t('placowkaPicker.filters.select')}
				clearLabel={t('placowkaPicker.filters.clear')}
			/>
			<PowiatFilterModal
				isOpen={isPowiatFilterOpen}
				onClose={() => setIsPowiatFilterOpen(false)}
				selectedPowiatId={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyPowiatFilter}
				title={t('placowkaPicker.filters.powiatTitle')}
				description={t('placowkaPicker.filters.powiatDescription')}
				applyLabel={t('placowkaPicker.filters.select')}
				clearLabel={t('placowkaPicker.filters.clear')}
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoFilterOpen}
				onClose={() => setIsWojewodztwoFilterOpen(false)}
				selectedWojewodztwoId={selectedWojewodztwoId}
				onApply={handleApplyWojewodztwoFilter}
				title={t('placowkaPicker.filters.voivodeshipTitle')}
				description={t('placowkaPicker.filters.voivodeshipDescription')}
				applyLabel={t('placowkaPicker.filters.select')}
				clearLabel={t('placowkaPicker.filters.clear')}
			/>
			<TypFilterModal
				isOpen={isTypFilterOpen}
				onClose={() => setIsTypFilterOpen(false)}
				selectedTypId={selectedTypId}
				onApply={handleApplyTypFilter}
				title={t('placowkaPicker.filters.typeTitle')}
				description={t('placowkaPicker.filters.typeDescription')}
				applyLabel={t('placowkaPicker.filters.select')}
				clearLabel={t('placowkaPicker.filters.clear')}
			/>
		</div>
	)
}
